import React, {
  createContext,
  type SetStateAction,
  useContext,
  useState,
} from "react";
import { createStore, StoreApi, useStore } from "zustand";
import { replaceEqualDeep } from "@src/lib/utils/replaceEqualDeep";
import {
  CourseValidationErrors,
  emptyCourseValidationErrors,
} from "@src/features/libraryItem/course/types/validation";
import { PromptTab } from "@src/features/libraryItem/contexts/LibraryItemQueryParamsContext";
import { ViewMode } from "@src/features/libraryItem/hooks/useViewModeQueryParams";
import { Version } from "@src/features/libraryItem/hooks/useVersionQueryParams";
import { AiRequestFragment } from "@src/features/libraryItem/course/hooks/useCourseVersionAIRequests.generated";
import { CourseVersionQuery } from "../build/CourseBuildContent.generated";
import { CommentThreadFragment } from "../build/comments/Comment.generated";
import {
  buildCourseScreens,
  CourseScreen,
  PromptScreenAttrs,
} from "../hooks/useCourseScreensAtom";

export type CourseBuilderCourseVersion =
  | CourseVersionQuery["AdminCourseVersion"]
  | undefined;

export type CourseBuilderUrlState = {
  activeScreenId: string | undefined;
  promptTab: PromptTab | undefined;
  viewMode: ViewMode;
  version: Version;
};

type ScreenInputs = {
  promptUuidsGeneratedByAI: Set<string>;
  commentThreads: CommentThreadFragment[] | undefined;
  aiRequests: AiRequestFragment[] | undefined;
};

type CourseBuilderStoreValues = CourseBuilderUrlState &
  ScreenInputs & {
    courseVersion: CourseBuilderCourseVersion;
    validationErrors: CourseValidationErrors;
    screens: CourseScreen[];
    screensById: Map<string, CourseScreen>;
    selectedScreenIds: string[];
    activeDragScreenId: string | null;
    courseSettingsOpen: boolean;
    shimmerOnChange: boolean;
    /**
     * The prompt whose media is in on-screen crop mode, if any. Crop can start
     * from the settings pane but happens on the media in the canvas.
     */
    croppingPromptUuid: string | null;
  };

export type CourseBuilderStoreState = CourseBuilderStoreValues & {
  setCourseVersion: (next: SetStateAction<CourseBuilderCourseVersion>) => void;
  setValidationErrors: (next: SetStateAction<CourseValidationErrors>) => void;
  setScreenInputs: (next: Partial<ScreenInputs>) => void;
  setPromptUuidsGeneratedByAI: (next: SetStateAction<Set<string>>) => void;
  syncFromUrl: (urlState: CourseBuilderUrlState) => void;
  setSelectedScreenIds: (ids: string[]) => void;
  setActiveDragScreenId: (id: string | null) => void;
  setCourseSettingsOpen: (open: boolean) => void;
  setShimmerOnChange: (value: boolean) => void;
  setCroppingPromptUuid: (uuid: string | null) => void;
  resetCourseBuilderStore: () => void;
};

const EMPTY_SCREENS: CourseScreen[] = [];

const resolve = <T>(next: SetStateAction<T>, prev: T): T =>
  typeof next === "function" ? (next as (prev: T) => T)(prev) : next;

const getInitialValues = (): CourseBuilderStoreValues => ({
  activeScreenId: undefined,
  promptTab: undefined,
  viewMode: "medium-screens",
  version: "draft",
  courseVersion: undefined,
  validationErrors: emptyCourseValidationErrors(),
  promptUuidsGeneratedByAI: new Set(),
  commentThreads: undefined,
  aiRequests: undefined,
  screens: EMPTY_SCREENS,
  screensById: new Map(),
  selectedScreenIds: [],
  activeDragScreenId: null,
  courseSettingsOpen: false,
  shimmerOnChange: false,
  croppingPromptUuid: null,
});

// Rebuilds the screen list, keeping the previous object for every screen whose
// content did not change so per-screen selectors stay referentially stable.
const withScreens = (
  prev: CourseBuilderStoreValues,
  next: Partial<CourseBuilderStoreValues>,
): Partial<CourseBuilderStoreValues> => {
  const merged = { ...prev, ...next };
  const { screens } = buildCourseScreens({
    lessons: merged.courseVersion?.lessons,
    validationErrors: merged.validationErrors,
    promptUuidsGeneratedByAI: merged.promptUuidsGeneratedByAI,
    commentThreads: merged.commentThreads,
    aiRequests: merged.aiRequests,
  });
  const sharedScreens = replaceEqualDeep(prev.screens, screens);
  if (sharedScreens === prev.screens) return next;
  return {
    ...next,
    screens: sharedScreens,
    screensById: new Map(sharedScreens.map((screen) => [screen.id, screen])),
  };
};

export const createCourseBuilderStore = (): StoreApi<CourseBuilderStoreState> =>
  createStore<CourseBuilderStoreState>()((set) => ({
    ...getInitialValues(),
    setCourseVersion: (next) => {
      set((state) => {
        const courseVersion = replaceEqualDeep(
          state.courseVersion,
          resolve(next, state.courseVersion),
        );
        if (courseVersion === state.courseVersion) return state;
        const isNewVersion = courseVersion?.id !== state.courseVersion?.id;
        return withScreens(state, {
          courseVersion,
          ...(isNewVersion
            ? {
                selectedScreenIds: [],
                activeDragScreenId: null,
                courseSettingsOpen: false,
                promptUuidsGeneratedByAI: new Set<string>(),
                croppingPromptUuid: null,
              }
            : {}),
        });
      });
    },
    setValidationErrors: (next) => {
      set((state) => {
        const validationErrors = resolve(next, state.validationErrors);
        if (validationErrors === state.validationErrors) return state;
        return withScreens(state, { validationErrors });
      });
    },
    setScreenInputs: (next) => {
      set((state) => {
        const changed = (Object.keys(next) as (keyof ScreenInputs)[]).some(
          (key) => next[key] !== state[key],
        );
        return changed ? withScreens(state, next) : state;
      });
    },
    setPromptUuidsGeneratedByAI: (next) => {
      set((state) => {
        const promptUuidsGeneratedByAI = resolve(
          next,
          state.promptUuidsGeneratedByAI,
        );
        return promptUuidsGeneratedByAI === state.promptUuidsGeneratedByAI
          ? state
          : withScreens(state, { promptUuidsGeneratedByAI });
      });
    },
    syncFromUrl: (urlState) => {
      set((state) =>
        state.activeScreenId === urlState.activeScreenId &&
        state.promptTab === urlState.promptTab &&
        state.viewMode === urlState.viewMode &&
        state.version === urlState.version
          ? state
          : {
              ...urlState,
              ...(state.activeScreenId !== urlState.activeScreenId ||
              state.promptTab !== urlState.promptTab
                ? { courseSettingsOpen: false }
                : {}),
            },
      );
    },
    setSelectedScreenIds: (selectedScreenIds) =>
      set({ selectedScreenIds, courseSettingsOpen: false }),
    setActiveDragScreenId: (activeDragScreenId) => set({ activeDragScreenId }),
    setCourseSettingsOpen: (courseSettingsOpen) => set({ courseSettingsOpen }),
    setShimmerOnChange: (shimmerOnChange) => set({ shimmerOnChange }),
    setCroppingPromptUuid: (croppingPromptUuid) => set({ croppingPromptUuid }),
    resetCourseBuilderStore: () => set(getInitialValues()),
  }));

// The builder uses one shared store. Read-only renderers that show several
// courses at once (the module PDF export) wrap each course in
// CourseBuilderStoreScope so every course gets its own store.
const sharedCourseBuilderStore = createCourseBuilderStore();

const CourseBuilderStoreScopeContext = createContext<
  StoreApi<CourseBuilderStoreState> | undefined
>(undefined);

export const CourseBuilderStoreScope = (props: {
  children?: React.ReactNode;
}): React.ReactElement => {
  const [store] = useState(createCourseBuilderStore);
  return React.createElement(
    CourseBuilderStoreScopeContext.Provider,
    { value: store },
    props.children,
  );
};

export const useCourseBuilderStoreApi = (): StoreApi<CourseBuilderStoreState> =>
  useContext(CourseBuilderStoreScopeContext) ?? sharedCourseBuilderStore;

const useCourseBuilderStoreHook = <T>(
  selector: (state: CourseBuilderStoreState) => T,
): T => useStore(useCourseBuilderStoreApi(), selector);

const useCourseBuilderStore = Object.assign(useCourseBuilderStoreHook, {
  getState: sharedCourseBuilderStore.getState,
  setState: sharedCourseBuilderStore.setState,
  subscribe: sharedCourseBuilderStore.subscribe,
});

export default useCourseBuilderStore;

// State of the shared builder store, for event handlers and effects.
export const getCourseBuilderState = (): CourseBuilderStoreState =>
  sharedCourseBuilderStore.getState();

// Selectors. Keep them module-level so components share identical functions.
export const selectCourseVersion = (
  s: CourseBuilderStoreState,
): CourseBuilderCourseVersion => s.courseVersion;
export const selectCourseVersionId = (
  s: CourseBuilderStoreState,
): number | undefined => s.courseVersion?.id;
type BuilderCourseVersion = NonNullable<CourseBuilderCourseVersion>;

export const selectCourse = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"] | undefined => s.courseVersion?.course;
export const selectCourseId = (
  s: CourseBuilderStoreState,
): number | undefined => s.courseVersion?.course.id;
export const selectLessons = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["lessons"] | undefined => s.courseVersion?.lessons;
export const selectDraftVersionSelected = (
  s: CourseBuilderStoreState,
): boolean =>
  !!s.courseVersion &&
  s.courseVersion.id === s.courseVersion.course.draftVersion.id;
export const selectPublishedVersionSelected = (
  s: CourseBuilderStoreState,
): boolean =>
  !!s.courseVersion &&
  s.courseVersion.id === s.courseVersion.course.publishedVersion?.id;
export const selectScreens = (s: CourseBuilderStoreState): CourseScreen[] =>
  s.screens;
export const selectActiveScreen = (
  s: CourseBuilderStoreState,
): CourseScreen | undefined => s.screensById.get(s.activeScreenId ?? "");

export const useCourseScreen = (
  screenId: string | undefined,
): CourseScreen | undefined =>
  useCourseBuilderStore((s) => s.screensById.get(screenId ?? ""));

export const usePromptScreenAttrs = (
  screenId: string | undefined,
): PromptScreenAttrs | undefined =>
  useCourseBuilderStore((s) => {
    const screen = s.screensById.get(screenId ?? "");
    return screen && "promptScreen" in screen ? screen.promptScreen : undefined;
  });

export const useActiveCourseScreen = (): CourseScreen | undefined =>
  useCourseBuilderStore(selectActiveScreen);

// A screen is highlighted when it is in the multi-selection, or when nothing is
// multi-selected and it is the active screen.
export const useIsCourseScreenSelected = (screenId: string): boolean =>
  useCourseBuilderStore((s) => {
    const hasLiveSelection = s.selectedScreenIds.some((id) =>
      s.screensById.has(id),
    );
    return hasLiveSelection
      ? s.selectedScreenIds.includes(screenId)
      : s.activeScreenId === screenId;
  });

// Selected screens in course order, ignoring ids of screens that no longer exist.
export const selectSelectedScreens = (
  s: CourseBuilderStoreState,
): CourseScreen[] =>
  s.screens.filter((screen) => s.selectedScreenIds.includes(screen.id));

export const useSelectedScreenCount = (): number =>
  useCourseBuilderStore((s) => selectSelectedScreens(s).length);

export const getSelectedScreens = (): CourseScreen[] =>
  selectSelectedScreens(useCourseBuilderStore.getState());

export const selectCourseLibraryItem = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["libraryItem"] | undefined =>
  s.courseVersion?.course.libraryItem;
export const selectCourseName = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["libraryItem"]["name"] | undefined =>
  s.courseVersion?.course.libraryItem.name;
export const selectCourseNameEn = (s: CourseBuilderStoreState): string =>
  s.courseVersion?.course.libraryItem.name?.en || "";
export const selectCourseDescription = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["libraryItem"]["description"] | undefined =>
  s.courseVersion?.course.libraryItem.description;
export const selectCourseDescriptionEn = (s: CourseBuilderStoreState): string =>
  s.courseVersion?.course.libraryItem.description?.en || "";
export const selectCourseCoverImage = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["libraryItem"]["coverImage"] | undefined =>
  s.courseVersion?.course.libraryItem.coverImage;
export const selectCoursePublishState = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["libraryItem"]["publishState"] | undefined =>
  s.courseVersion?.course.libraryItem.publishState;
export const selectCoursePublishedVersion = (
  s: CourseBuilderStoreState,
): BuilderCourseVersion["course"]["publishedVersion"] | undefined =>
  s.courseVersion?.course.publishedVersion;
export const selectCourseVersionCreatedAt = (
  s: CourseBuilderStoreState,
): string | undefined => s.courseVersion?.createdAt;
